import { useQuery } from '@tanstack/react-query';
import type { BotOverviewDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';
import { shouldPollOverview } from '../lib/overview.ts';

export interface UseBotOverviewOptions {
  /**
   * Opt-in polling while a source is still `pending`/`crawling`/`indexing` (dashboard-shell
   * feedback cycle, Task 2). A crawl finishes server-side with no client action to hang an
   * invalidation off, so `BotOverview` — the one screen actually looking at that state — passes
   * `true` to keep the Knowledge card and stats moving on their own while it's open. `RailNav`
   * (below) does NOT pass this: it's the one observer that's mounted on every route for the
   * lifetime of the session, so an unconditional poll there would run against bots nobody is
   * looking at. Stops itself once `knowledge.anyInFlight` is `false` — which is also `false`
   * for a zero-source bot (nothing has ever been in flight) and for a bot where every source
   * has settled into `ready`/`error` (never spins on a source that failed and needs a manual
   * resync). Deliberately NOT `worstStatus === 'error'`: severity ranks `error` above
   * `crawling`, so a bot with one failed source and one still-crawling source would stop the
   * poll on the exact crawl it exists to watch — see `anyInFlight`'s doc comment in
   * `BotOverviewDto`.
   */
  pollWhileSyncing?: boolean;
}

/**
 * `GET /api/bots/:botId/overview` — shared between `BotOverview` (the full screen) and the
 * rail's `RailNav` (just the Unanswered item's amber count pill, Task 7), so both read the
 * same TanStack Query cache entry instead of firing two independent fetches (spec D2/D8, M5
 * task-1; dashboard-shell cycle README §8).
 *
 * Freshness (dashboard-shell feedback cycle, Task 2): this used to carry `staleTime: 120_000` +
 * `refetchOnWindowFocus: false` because `BotHeader` mounted this same query on all eight bot
 * tabs, and a short staleTime meant refetching the full aggregate (14 days of messages, latest
 * conversations, source rollup) on every tab click. **That component is gone** — `RailNav` is
 * the only always-mounted observer now (it lives in `Protected`, a sibling of `<Outlet>`, so it
 * never remounts on tab navigation within a bot), and `BotOverview` mounts a second observer
 * only while its own tab is open. Tab-clicking no longer multiplies fetches, so the throttle's
 * original reason is gone.
 *
 * What replaced it: the places that actually change this data now invalidate
 * `['bots', botId, 'overview']` directly the moment they act — a Playground message completing
 * (`Playground.tsx`), a source added/deleted/resynced (`BotSources.tsx`, `BotOverview.tsx`'s
 * bulk re-sync) — the same pattern `useAnswerUnanswered` already used for answering an
 * unanswered question. `staleTime` is cut to 30s (not 0 — still a real bound against back-to-
 * back re-fetches if Overview is mounted/unmounted in a tight loop) purely as a backstop for
 * changes with no client-side action to invalidate on (a lead captured by a visitor on the
 * public widget while the owner is looking at Overview). `refetchOnWindowFocus` is now `true`:
 * with only one persistent observer left, a refetch on tab/window focus is cheap and closes the
 * gap for anything that changed while the dashboard tab was in the background.
 */
export function useBotOverview(botId: string | undefined, options?: UseBotOverviewOptions) {
  return useQuery({
    queryKey: ['bots', botId, 'overview'],
    queryFn: () => apiFetch<BotOverviewDto>(`/api/bots/${botId}/overview`),
    enabled: !!botId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: options?.pollWhileSyncing
      ? (query) => (shouldPollOverview(query.state.data?.knowledge) ? 4000 : false)
      : false,
  });
}
